// Helper functions for ComplianceTrainingPage

import { ProviderDto } from '@ampsec/platform-client';
import { ComplianceTrainingSubject } from 'Core-utils/i18n/complianceTrainingModuleI18n';
import { TrainingModuleState } from './types';

export const DEFAULT_LAUNCH_NOW_COOLDOWN_MS = 1_800_000;
export const COMPLIANCE_TRAINING_LAUNCH_COOLDOWN_STORAGE_KEY = 'compliance_training_launch_cooldown_v1';

type LaunchCooldownMap = Record<string, number>;

const AMPLIFIER_PROVIDER_NAME = 'AMPLIFIER';

export const findAmplifierProviderId = (providers: ProviderDto[] | undefined): string | undefined => {
    if (!Array.isArray(providers)) {
        return undefined;
    }
    return providers.find((p) => p.displayValue === AMPLIFIER_PROVIDER_NAME)?.id;
};

export const isApiResponseSuccess = (apiRes: any): boolean => {
    const hasStatus = typeof apiRes?.status === 'number';
    const statusOk = hasStatus && apiRes.status >= 200 && apiRes.status < 300;
    const dataOk = apiRes?.data !== undefined && apiRes?.data !== null;
    return Boolean(apiRes) && (statusOk || dataOk);
};

export const extractErrorMessage = (apiRes: any, defaultMessage: string): string => {
    if (!apiRes) {
        return defaultMessage;
    }
    return apiRes.response?.data?.message || apiRes.message || apiRes.data?.message || defaultMessage;
};

/**
 * Toggles a module's enabled state in the modules array
 */
export const toggleModuleEnabled = (modules: any[], subjectId: string): any[] => {
    return modules.map((m) => (m.subject === subjectId ? { ...m, enabled: !m.enabled } : m));
};

/**
 * Gets enabled subject IDs from modules array (deduplicated)
 */
export const getEnabledSubjectIds = (modules: any[]): string[] => {
    const enabledSubjects = modules.filter((m) => m.enabled).map((m) => m.subject);
    return Array.from(new Set(enabledSubjects));
};

/**
 * Sets isLaunching flag for a module
 */
export const setModuleLaunchingState = (modules: any[], subjectId: string, isLaunching: boolean): any[] => {
    return modules.map((m) => (m.subject === subjectId ? { ...m, isLaunching } : m));
};

/**
 * Updates overdue days for a module
 */
export const updateModuleOverdueDays = (modules: any[], subjectId: string, days: string): any[] => {
    return modules.map((m) => (m.subject === subjectId ? { ...m, overdueInDays: days } : m));
};

export const parseLaunchNowCooldownMs = (raw: string | undefined): number => {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LAUNCH_NOW_COOLDOWN_MS;
};

const readLaunchCooldownMap = (): LaunchCooldownMap => {
    try {
        const raw = window.localStorage.getItem(COMPLIANCE_TRAINING_LAUNCH_COOLDOWN_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as LaunchCooldownMap;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeLaunchCooldownMap = (cooldowns: LaunchCooldownMap): void => {
    try {
        if (Object.keys(cooldowns).length === 0) {
            window.localStorage.removeItem(COMPLIANCE_TRAINING_LAUNCH_COOLDOWN_STORAGE_KEY);
            return;
        }
        window.localStorage.setItem(COMPLIANCE_TRAINING_LAUNCH_COOLDOWN_STORAGE_KEY, JSON.stringify(cooldowns));
    } catch {
        // ignore storage errors
    }
};

export const pruneExpiredLaunchCooldowns = (): LaunchCooldownMap => {
    const now = Date.now();
    const cooldowns = readLaunchCooldownMap();
    const activeCooldowns = Object.entries(cooldowns).reduce<LaunchCooldownMap>((acc, [subjectId, expiresAt]) => {
        if (typeof expiresAt === 'number' && Number.isFinite(expiresAt) && expiresAt > now) {
            acc[subjectId] = expiresAt;
        }
        return acc;
    }, {});
    writeLaunchCooldownMap(activeCooldowns);
    return activeCooldowns;
};

export const getLaunchCooldownExpiresAt = (subjectId: string): number | null => {
    const cooldowns = pruneExpiredLaunchCooldowns();
    const expiresAt = cooldowns[subjectId];
    return typeof expiresAt === 'number' && Number.isFinite(expiresAt) ? expiresAt : null;
};

export const setLaunchCooldownExpiresAt = (subjectId: string, expiresAt: number): void => {
    const cooldowns = pruneExpiredLaunchCooldowns();
    cooldowns[subjectId] = expiresAt;
    writeLaunchCooldownMap(cooldowns);
};

export const clearLaunchCooldownExpiresAt = (subjectId: string): void => {
    const cooldowns = pruneExpiredLaunchCooldowns();
    delete cooldowns[subjectId];
    writeLaunchCooldownMap(cooldowns);
};

export const applyStoredLaunchCooldowns = (modules: TrainingModuleState[]): TrainingModuleState[] => {
    const cooldowns = pruneExpiredLaunchCooldowns();
    return modules.map((module) => ({
        ...module,
        launchCooldownExpiresAt: cooldowns[module.subject] ?? null,
    }));
};

const mergeActiveLaunchCooldownExpiry = (
    storedExpiry: number | null,
    existingExpiry: number | null | undefined,
    now: number,
): number | null => {
    const activeExpiryCandidates = [storedExpiry, existingExpiry ?? null].filter(
        (expiresAt): expiresAt is number => typeof expiresAt === 'number' && expiresAt > now,
    );

    if (activeExpiryCandidates.length === 0) {
        return storedExpiry;
    }

    return Math.max(...activeExpiryCandidates);
};

export const buildComplianceTrainingModulesState = (
    rawSubjects: ComplianceTrainingSubject[],
    activeSubjects: string[],
    previousModules: TrainingModuleState[],
    defaultOverdueDays: string,
): TrainingModuleState[] => {
    const previousBySubject = new Map(previousModules.map((module) => [module.subject, module]));
    const now = Date.now();

    const initializedModules: TrainingModuleState[] = rawSubjects.map((subject) => {
        const existing = previousBySubject.get(subject.subject);

        return {
            ...subject,
            enabled: activeSubjects.includes(subject.subject),
            overdueInDays: existing?.overdueInDays ?? defaultOverdueDays,
            isLaunching: existing?.isLaunching ?? false,
            launchCooldownExpiresAt: existing?.launchCooldownExpiresAt ?? null,
            thumbnail: subject.thumbnail || '',
        };
    });

    return applyStoredLaunchCooldowns(initializedModules).map((module) => {
        const existing = previousBySubject.get(module.subject);
        const mergedExpiry = mergeActiveLaunchCooldownExpiry(
            module.launchCooldownExpiresAt,
            existing?.launchCooldownExpiresAt,
            now,
        );

        if (mergedExpiry === module.launchCooldownExpiresAt) {
            return module;
        }

        return {
            ...module,
            launchCooldownExpiresAt: mergedExpiry,
        };
    });
};

export const setModuleLaunchCooldown = (
    modules: TrainingModuleState[],
    subjectId: string,
    expiresAt: number | null,
): TrainingModuleState[] => {
    return modules.map((module) =>
        module.subject === subjectId ? { ...module, launchCooldownExpiresAt: expiresAt } : module,
    );
};

export const formatLaunchCooldownExpiry = (expiresAt: number): string => {
    return new Date(expiresAt).toLocaleString();
};

export const isModuleInLaunchCooldown = (module: Pick<TrainingModuleState, 'launchCooldownExpiresAt'>): boolean => {
    return typeof module.launchCooldownExpiresAt === 'number' && module.launchCooldownExpiresAt > Date.now();
};
